import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export const storeSearchSchema = z.object({
  sapErp: z.string().optional(),
  banner: z.array(z.string()).optional(),
  province: z.array(z.string()).optional(),
  area: z.array(z.string()).optional(),
  region: z.array(z.string()).optional(),
  territory: z.array(z.string()).optional(),
  ...datatableSearchSchema.shape,
})

export const allStoresQueries = { page: -1, sort: { id: 'name', desc: false } }

export type StoreSearch = z.infer<typeof storeSearchSchema>

export async function getStores(search: StoreSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.stores.$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  const stores = await res.json()
  return stores
}

export async function getStoresOptions(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.stores.options.$get({}, { init: { signal } })

  const storesOptions = (await res.json()).map((erp) => ({
    value: erp.value ?? '',
    label: erp.label ?? '',
    keywords: erp.keywords ?? [],
  }))
  return storesOptions
}

export async function getStoreDetails(id: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.stores[':id'].$get({ param: { id: String(id) } }, { init: { signal } })

  const storeDetails = await res.json()
  return storeDetails
}

export const storesQueryOptions = (queries: StoreSearch) => ({
  queryKey: ['stores', queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getStores(queries, signal),
})

export function useStores(queries: StoreSearch) {
  return useQuery(storesQueryOptions(queries))
}

export const storeDetailsQueryOptions = (id: string) => ({
  queryKey: ['storeDetails', { id }],
  queryFn: ({ signal }: { signal: AbortSignal }) => getStoreDetails(id, signal),
})

export function useStoreDetails(id: string) {
  return useQuery(storeDetailsQueryOptions(id))
}

export function useStoresOptions() {
  return useQuery({
    queryKey: ['storesOptions'],
    queryFn: ({ signal }) => getStoresOptions(signal),
  })
}
